import { ProductAmountSourceMap } from '@app/common/constants/productAmountSource'

/**
 * Gets the override source tracking for a product
 * @param {Object} product - Product object
 * @param {Array} selectedAppraisers - Array of selected appraisers
 * @param {Object} productOverrideDetails - Map of product ID to override details
 * @returns {Object|null} Override source tracking object or null
 */
export const getOverrideSourceTracking = (product, selectedAppraisers, productOverrideDetails) => {
  if (!product || !productOverrideDetails) {
    return null
  }

  // If product has manual fee override, return manual override source tracking immediately
  if (product.manual_fee_override_exists) {
    return {
      manual_fee_override_exists: true,
      integration_fee_applied: false,
      borrower_amount_override: false,
      fee_mgmt_override: false,
      legacy_vendor_geo_override: false,
      appraiser_override: false,
      legacy_geo_override: false,
      disclosed_amount_cap_applied: false,
      base_product_amount: false,
      fee_mgmt_override_sheet_name: '',
      integration_fee_error_msg: '',
    }
  }

  let sourceTracking = null

  // If there are selected appraisers, use the first appraiser's override source tracking
  if (selectedAppraisers && selectedAppraisers.length > 0) {
    const firstAppraiserId = selectedAppraisers[0].id
    const appraiserSpecificKey = `${product.id}_${firstAppraiserId}`
    sourceTracking = productOverrideDetails[appraiserSpecificKey]?.overrideSourceTracking
  }

  // Fall back to general product override source tracking
  if (!sourceTracking) {
    sourceTracking = productOverrideDetails[product.id]?.overrideSourceTracking
  }

  return sourceTracking
}

/**
 * Converts override source tracking object to readable array of source strings
 * @param {Object} overrideSourceTracking - Override source tracking object
 * @returns {Array} Array of readable source strings
 */
export const getReadableOverrideSource = (overrideSourceTracking) => {
  if (!overrideSourceTracking) {
    return []
  }

  const sources = []

  // Add readable versions of true values
  Object.keys(ProductAmountSourceMap).forEach((key) => {
    if (overrideSourceTracking[key] === true) {
      sources.push(ProductAmountSourceMap[key])
    }
  })

  // Handle fee_mgmt_override_sheet_name specially
  if (
    overrideSourceTracking.fee_mgmt_override === true &&
    overrideSourceTracking.fee_mgmt_override_sheet_name &&
    overrideSourceTracking.fee_mgmt_override_sheet_name.trim() !== ''
  ) {
    // Replace or append the fee management override with the sheet name
    const sheetNameIndex = sources.indexOf('Fee Override')
    if (sheetNameIndex !== -1) {
      const enhancedSource = `Fee Override (${overrideSourceTracking.fee_mgmt_override_sheet_name})`
      sources[sheetNameIndex] = enhancedSource
    }
  }

  return sources
}

/**
 * Calculates the product amount for a specific appraiser based on override priority
 * Priority: 1) Manual override, 2) Appraiser-specific override, 3) Base amount
 * @param {Object} product - Selected product
 * @param {Object} appraiser - Appraiser object
 * @param {Object} overrideData - Appraiser-specific override data
 * @param {Array} products - Full list of products
 * @param {Array} originalProducts - Original products array
 * @param {Object} productOverrideDetails - Product override details map
 * @returns {number} Calculated product amount
 */
export const calculateProductAmountForAppraiser = (
  product,
  appraiser,
  overrideData,
  products,
  originalProducts,
  productOverrideDetails,
) => {
  const productAmount = product.amount // Default to current amount

  // PRIORITY 1: Manual fee override - if user manually entered a fee, use it for ALL appraisers
  if (product.manual_fee_override_exists) {
    return product.amount
  }

  // PRIORITY 2: Appraiser-specific override - if appraiser has specific override for this product
  if (Object.keys(overrideData).length > 0 && overrideData[product.id]) {
    const overrideAmount = overrideData[product.id]
    const selectedFullListProduct = products.find((fullListProduct) => fullListProduct.id === product.id)

    // Check for integration fee error in productOverrideDetails
    const appraiserSpecificKey = `${product.id}_${appraiser.id}`
    const overrideSourceTracking =
      productOverrideDetails[appraiserSpecificKey]?.overrideSourceTracking ||
      productOverrideDetails[product.id]?.overrideSourceTracking
    const hasIntegrationError = overrideSourceTracking?.integration_fee_error_msg?.trim()

    if (hasIntegrationError) {
      // If there's an integration error, use the original product amount
      const originalAmount =
        selectedFullListProduct?.original_amount !== undefined
          ? selectedFullListProduct.original_amount
          : product.amount
      return originalAmount
    }

    if (
      selectedFullListProduct &&
      selectedFullListProduct.original_amount &&
      (selectedFullListProduct.original_amount === overrideAmount ||
        selectedFullListProduct.amount === overrideAmount)
    ) {
      return selectedFullListProduct.amount
    }

    return overrideAmount
  }

  // PRIORITY 3: Base amount - use original product amount without any appraiser-specific overrides
  // Try to find the original product amount from originalProducts
  if (originalProducts && Array.isArray(originalProducts)) {
    const originalProduct = originalProducts.find((p) => p.id === product.id)
    if (originalProduct) {
      return originalProduct.amount
    }
  }

  // Fallback: use processed product's original_amount if available
  const processedProduct = products.find((p) => p.id === product.id)
  if (processedProduct && processedProduct.original_amount !== undefined) {
    return processedProduct.original_amount
  }

  return productAmount
}

/**
 * Normalizes a product amount to a numeric value, handling null/undefined/empty strings
 * @param {*} amount - Product amount (can be string, number, null, undefined, or empty string)
 * @returns {number} Numeric amount (0 if invalid)
 */
export const normalizeProductAmount = (amount) => {
  if (amount === null || amount === undefined || amount === '') {
    return 0
  }
  return parseFloat(amount) || 0
}
